// Phones: antd Select / TreeSelect / Cascader open as bottom sheets (styled by
// each app — BO mobilePopups.less, B2C mobileSelectSheet.less). The field
// sits under the sheet (and the keyboard), so this runtime gives the sheet a
// head of its own (selectSheetSearch.less):
//
//   ╭──────────── ▬ ────────────╮
//   │ [🔍 par|                ]  │  ← search bar: what is typed in the field
//   │ (Paris ×)(Rome ×)(Oslo ×)→ │  ← selectedChips: the picked values,
//   │  Paris                  ✓  │    scrolling sideways
//   │  Parma                     │
//   ╰────────────────────────────╯
//
//   • the head lives in the popup element rc-select owns (the dropdown's only
//     child) — a tap on the dropdown itself counts as an outside click for
//     rc-select (it closes) — and its mouse-down is cancelled, so focus (and
//     the keyboard) stays in the field: on touch devices rc-select does not
//     take focus back after a tap in the sheet;
//   • default: a tap opens only the sheet — the field's search input is
//     read-only (inputmode none), no keyboard; tapping the search bar unlocks
//     the field and raises the keyboard; when the select closes, the field is
//     locked again;
//   • typeOnOpen: the field is never locked — a tap opens the sheet and the
//     keyboard together; the search bar raises the keyboard again if it was
//     dismissed; picking (a single select closes) drops the keyboard;
//   • selectedChips: multiple selects list their picked values as chips —
//     × removes one, a newly picked one scrolls into view — so the sheet
//     stays open while the user picks one value after another.
//
// Not touched: AutoComplete (typing is the whole point and it has no list
// before typing) and free-text tag inputs with no options — TopFilters /
// renderInput SEARCH_TAG (id select-tag-*) or any select with the class
// `m-free-text`.
// One install per app, phones only; returns the uninstall function.

// the search input of any select field (searchable or not)
const FIELD_INPUT =
  '.ant-select:not(.ant-select-auto-complete) .ant-select-selection-search-input';
const SEARCH_INPUT =
  '.ant-select-show-search:not(.ant-select-auto-complete):not(.ant-select-disabled) .ant-select-selection-search-input';
// free-text tag inputs — typing is the input
const FREE_TEXT_INPUT = '[id^="select-tag-"], .m-free-text input';
const OPEN_DROPDOWN = '.ant-select-dropdown:not(.ant-select-dropdown-hidden)';
// a multiple select's tags (rc-overflow items), not the search input
const TAG_ITEM =
  '.ant-select-selection-overflow-item:not(.ant-select-selection-overflow-item-suffix)';
const REST_ITEM_CLASS = 'ant-select-selection-overflow-item-rest';

const HEAD_CLASS = 'm-select-head';
const SEARCH_CLASS = `${HEAD_CLASS}__search`;
const SEARCH_TEXT_CLASS = `${HEAD_CLASS}__search-text`;
const CHIPS_CLASS = `${HEAD_CLASS}__chips`;
const CHIP_CLASS = 'm-select-chip';
const LOCKED_FLAG = 'mSearchLocked';
const ARMED_FLAG = 'mSearchArmed';
// the keyboard is at least this tall — smaller gaps are browser toolbars
const KEYBOARD_MIN = 80;

export interface SelectSheetOptions {
  /** Dropdowns with this class are not sheets: no head, their field is never locked. */
  noSheetClass: string;
  /** A tap opens the sheet and the keyboard together — the field is never locked. */
  typeOnOpen?: boolean;
  /** Multiple selects: the picked values as removable chips in the sheet's head. */
  selectedChips?: boolean;
  /** The search bar's text when nothing is typed and the field has no placeholder. */
  searchLabel?: string;
  /** The accessible name of a chip's remove button (followed by the value). */
  removeLabel?: string;
}

interface ChipItem {
  label: string;
  // the tag's own remove button in the field
  remove: HTMLElement | null;
}

const isFieldInput = (el: Element | null): el is HTMLInputElement =>
  !!el &&
  el instanceof HTMLInputElement &&
  el.matches(FIELD_INPUT) &&
  !el.matches(FREE_TEXT_INPUT);

const isLockable = (el: Element | null): el is HTMLInputElement =>
  isFieldInput(el) && el.matches(SEARCH_INPUT);

const lock = (input: HTMLInputElement) => {
  input.readOnly = true;
  input.setAttribute('inputmode', 'none');
  input.dataset[LOCKED_FLAG] = '1';
  delete input.dataset[ARMED_FLAG];
};

const unlock = (input: HTMLInputElement) => {
  input.readOnly = false;
  input.removeAttribute('inputmode');
  delete input.dataset[LOCKED_FLAG];
};

const isSelectOpen = (input: HTMLInputElement) =>
  !!input.closest('.ant-select-open');

const isKeyboardUp = () => {
  const viewport = window.visualViewport;
  return (
    !!viewport &&
    window.innerHeight - viewport.height - viewport.offsetTop > KEYBOARD_MIN
  );
};

// The open dropdown of a select field: by its listbox id, else — antd keeps
// focus in the field while it is open — the one visible select dropdown.
const dropdownOf = (input: HTMLInputElement): HTMLElement | null => {
  const listId =
    input.getAttribute('aria-controls') || input.getAttribute('aria-owns');
  const byId = listId
    ? document.getElementById(listId)?.closest<HTMLElement>('.ant-select-dropdown')
    : null;
  if (byId && !byId.classList.contains('ant-select-dropdown-hidden')) return byId;
  const open = Array.from(
    document.querySelectorAll<HTMLElement>(OPEN_DROPDOWN)
  ).filter((el) => el.getBoundingClientRect().height > 0);
  return open.length === 1 ? open[0] : null;
};

const placeholderOf = (input: HTMLInputElement) =>
  input
    .closest('.ant-select')
    ?.querySelector('.ant-select-selection-placeholder')
    ?.textContent?.trim() || '';

// A multiple select's picked values, in order. maxTagCount="responsive"
// keeps every tag in the DOM (the ones that do not fit are hidden and counted
// by the "+ N" tag); a numeric maxTagCount renders only the first N — then
// "+ N" stands for the others and is shown as a chip of its own.
const chipItemsOf = (input: HTMLInputElement): ChipItem[] => {
  const select = input.closest('.ant-select-multiple');
  if (!select) return [];
  const items = Array.from(select.querySelectorAll<HTMLElement>(TAG_ITEM));
  const tags = items.filter((el) => !el.classList.contains(REST_ITEM_CLASS));
  const rest = items.find((el) => el.classList.contains(REST_ITEM_CLASS));
  const isHidden = (el: HTMLElement) => el.style.opacity === '0';
  const chips = tags.map((tag) => ({
    label: (
      tag.querySelector('.ant-select-selection-item-content')?.textContent ??
      tag.textContent ??
      ''
    ).trim(),
    remove: tag.querySelector<HTMLElement>(
      '.ant-select-selection-item-remove, .ant-tag-close-icon'
    ),
  }));
  if (rest && !isHidden(rest) && !tags.some(isHidden)) {
    chips.push({ label: (rest.textContent ?? '').trim(), remove: null });
  }
  return chips.filter((chip) => chip.label);
};

const setText = (el: Element | null, text: string) => {
  if (el && el.textContent !== text) el.textContent = text;
};

const setHidden = (el: HTMLElement | null, hidden: boolean) => {
  if (el && el.hidden !== hidden) el.hidden = hidden;
};

const setData = (el: HTMLElement, key: string, value: string) => {
  if (el.dataset[key] !== value) el.dataset[key] = value;
};

const buildHead = () => {
  const head = document.createElement('div');
  head.className = HEAD_CLASS;
  // an echo of the field — screen readers have the field itself
  const search = document.createElement('div');
  search.className = SEARCH_CLASS;
  search.setAttribute('aria-hidden', 'true');
  search.hidden = true;
  const text = document.createElement('span');
  text.className = SEARCH_TEXT_CLASS;
  search.appendChild(text);
  const chips = document.createElement('div');
  chips.className = CHIPS_CLASS;
  chips.hidden = true;
  head.append(search, chips);
  return head;
};

const buildChip = (item: ChipItem, index: number, removeLabel: string) => {
  const chip = document.createElement('span');
  chip.className = CHIP_CLASS;
  const label = document.createElement('span');
  label.className = `${CHIP_CLASS}__label`;
  label.textContent = item.label;
  chip.appendChild(label);
  if (item.remove) {
    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = `${CHIP_CLASS}__remove`;
    remove.dataset.index = String(index);
    remove.setAttribute('aria-label', `${removeLabel} ${item.label}`.trim());
    chip.appendChild(remove);
  } else {
    chip.classList.add(`${CHIP_CLASS}--static`);
  }
  return chip;
};

// Bring the last chip (rc-select appends a newly picked value) into view —
// the strip only, never the sheet or the page. Works in RTL too: the deltas
// are measured, not derived from scrollLeft. Two frames on: a smooth scroll
// started in the frame of the pick is cancelled (the tap, rc-select's
// re-render).
const revealLastChip = (strip: HTMLElement) => {
  const reveal = () => {
    const last = strip.lastElementChild;
    if (!strip.isConnected || !last) return;
    const box = strip.getBoundingClientRect();
    const chip = last.getBoundingClientRect();
    const gap = 14;
    let delta = 0;
    if (chip.right > box.right - gap) delta = chip.right - box.right + gap;
    else if (chip.left < box.left + gap) delta = chip.left - box.left - gap;
    if (delta) strip.scrollBy({ left: delta, behavior: 'smooth' });
  };
  window.requestAnimationFrame(() => window.requestAnimationFrame(reveal));
};

// Type `value` into a select's search input the way React notices (its
// value tracker ignores a plain assignment).
const retype = (input: HTMLInputElement, value: string) => {
  Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set?.call(
    input,
    value
  );
  input.dispatchEvent(new Event('input', { bubbles: true }));
};

export const installSelectSheetRuntime = ({
  noSheetClass,
  typeOnOpen = false,
  selectedChips = false,
  searchLabel = '',
  removeLabel = '',
}: SelectSheetOptions) => {
  if (typeof document === 'undefined') return () => undefined;

  let frame = 0;
  // the field the open sheet belongs to — kept while focus is elsewhere
  // (iOS can leave it on <body> after a tap in the sheet)
  let lastInput: HTMLInputElement | null = null;
  const fieldOfHead = new WeakMap<HTMLElement, HTMLInputElement>();

  const syncSearch = (
    head: HTMLElement,
    input: HTMLInputElement | null,
    active: Element | null
  ) => {
    const search = head.querySelector<HTMLElement>(`.${SEARCH_CLASS}`);
    if (!search) return;
    setHidden(search, !input);
    if (!input) return;
    let state = 'idle';
    if (input.value) state = 'typed';
    else if (input === active && input.dataset[ARMED_FLAG]) state = 'empty';
    setData(search, 'state', state);
    setText(
      search.querySelector(`.${SEARCH_TEXT_CLASS}`),
      input.value || placeholderOf(input) || searchLabel || '…'
    );
  };

  const syncChips = (head: HTMLElement, items: ChipItem[]) => {
    const strip = head.querySelector<HTMLElement>(`.${CHIPS_CLASS}`);
    if (!strip) return;
    setHidden(strip, !items.length);
    const signature = items
      .map((item) => `${item.remove ? '×' : '·'}${item.label}`)
      .join('\n');
    if (strip.dataset.signature === signature) return;
    const grew =
      strip.dataset.signature !== undefined &&
      items.length > Number(strip.dataset.count || 0);
    strip.dataset.signature = signature;
    strip.dataset.count = String(items.length);
    strip.textContent = '';
    items.forEach((item, index) =>
      strip.appendChild(buildChip(item, index, removeLabel))
    );
    if (grew) {
      strip.lastElementChild?.classList.add(`${CHIP_CLASS}--new`);
      revealLastChip(strip);
    }
  };

  const sync = () => {
    frame = 0;
    const active = document.activeElement;

    document.querySelectorAll<HTMLInputElement>(SEARCH_INPUT).forEach((input) => {
      if (input.matches(FREE_TEXT_INPUT)) return;
      const armed = !!input.dataset[ARMED_FLAG];
      if (armed && input === active && isSelectOpen(input)) return;
      if (armed && input === active) {
        // closed after picking: drop the keyboard (and lock the field again)
        if (typeOnOpen) delete input.dataset[ARMED_FLAG];
        else lock(input);
        input.blur();
        return;
      }
      if (typeOnOpen) {
        delete input.dataset[ARMED_FLAG];
        return;
      }
      // lock every searchable field that is not being typed into right now
      if (!input.dataset[LOCKED_FLAG] || !input.readOnly) lock(input);
    });

    // the heads of closed dropdowns go — the next open starts fresh
    document
      .querySelectorAll<HTMLElement>(`.ant-select-dropdown-hidden .${HEAD_CLASS}`)
      .forEach((head) => head.remove());

    let input: HTMLInputElement | null = null;
    if (isFieldInput(active) && isSelectOpen(active)) input = active;
    else if (lastInput?.isConnected && isSelectOpen(lastInput)) input = lastInput;
    lastInput = input;
    if (!input) return;
    const dropdown = dropdownOf(input);
    if (!dropdown) return;
    if (dropdown.classList.contains(noSheetClass)) {
      // not a sheet: a plain dropdown under the field — let it be typed into
      if (input.dataset[LOCKED_FLAG]) unlock(input);
      return;
    }
    const popup = dropdown.firstElementChild as HTMLElement | null;
    if (!popup) return;

    const searchable = isLockable(input);
    const chips = selectedChips ? chipItemsOf(input) : [];
    let head = popup.querySelector<HTMLElement>(`:scope > .${HEAD_CLASS}`);
    if (!searchable && !chips.length) {
      head?.remove();
      return;
    }
    if (!head) {
      head = buildHead();
      popup.insertBefore(head, popup.firstChild);
    }
    fieldOfHead.set(head, input);
    if (typeOnOpen && searchable && input === active) {
      input.dataset[ARMED_FLAG] = '1';
    }
    syncSearch(head, searchable ? input : null, active);
    syncChips(head, chips);
  };

  const schedule = () => {
    if (!frame) frame = window.requestAnimationFrame(sync);
  };

  // Unlock the field and raise the keyboard. blur + focus inside the tap is
  // what makes iOS show it for an already-focused field; rc-select cancels
  // its close-on-blur when focus returns in the same tick. Not while the
  // keyboard is already up. A multiple select clears its search on the blur
  // — what was typed is typed back once that has rendered.
  const raiseKeyboard = (input: HTMLInputElement) => {
    unlock(input);
    input.dataset[ARMED_FLAG] = '1';
    if (input !== document.activeElement || !isKeyboardUp()) {
      const typed = input.value;
      input.blur();
      input.focus();
      if (typed) {
        window.setTimeout(() => {
          if (input.isConnected && isSelectOpen(input) && !input.value) {
            retype(input, typed);
          }
        });
      }
    }
    schedule();
  };

  // A mouse-down in the head would move focus out of the field (rc-select
  // only prevents that in its option list) — and drop the keyboard.
  const onMouseDown = (event: MouseEvent) => {
    if ((event.target as Element | null)?.closest?.(`.${HEAD_CLASS}`)) {
      event.preventDefault();
    }
  };

  // A tap on the search bar raises the keyboard; on a chip's × it removes
  // that value (through the tag's own remove button in the field).
  const onClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement | null;
    const head = target?.closest?.<HTMLElement>(`.${HEAD_CLASS}`);
    if (!target || !head) return;
    event.preventDefault();
    const input = fieldOfHead.get(head);
    if (!input?.isConnected) return;
    const remove = target.closest<HTMLElement>(`.${CHIP_CLASS}__remove`);
    if (remove) {
      chipItemsOf(input)[Number(remove.dataset.index)]?.remove?.click();
      return;
    }
    if (target.closest(`.${SEARCH_CLASS}`) && isLockable(input)) {
      raiseKeyboard(input);
    }
  };

  sync();
  const observer = new MutationObserver(schedule);
  observer.observe(document.body, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['class'],
  });
  document.addEventListener('input', schedule, true);
  document.addEventListener('focusin', schedule, true);
  document.addEventListener('focusout', schedule, true);
  document.addEventListener('mousedown', onMouseDown, true);
  document.addEventListener('click', onClick, true);

  return () => {
    if (frame) window.cancelAnimationFrame(frame);
    observer.disconnect();
    document.removeEventListener('input', schedule, true);
    document.removeEventListener('focusin', schedule, true);
    document.removeEventListener('focusout', schedule, true);
    document.removeEventListener('mousedown', onMouseDown, true);
    document.removeEventListener('click', onClick, true);
    // leave every field editable again (desktop / unmount)
    document
      .querySelectorAll<HTMLInputElement>(SEARCH_INPUT)
      .forEach((input) => {
        if (input.dataset[LOCKED_FLAG]) unlock(input);
        delete input.dataset[ARMED_FLAG];
      });
    document
      .querySelectorAll(`.${HEAD_CLASS}`)
      .forEach((head) => head.remove());
  };
};

export default installSelectSheetRuntime;
